import React from "react";
import { Table, Tag, Space, Button, Tooltip, Popconfirm } from "antd";
import {
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  SendOutlined,
  InboxOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

const STATUS_TAGS = {
  Draft: { color: "default", label: "Draft" },
  "Pending HoD Review": { color: "orange", label: "Pending HoD" },
  "Pending Section Head Validation": {
    color: "orange",
    label: "Pending Section Head",
  },
  "Pending Deputy Head Approval": {
    color: "orange",
    label: "Pending Deputy Head",
  },
  Rejected: { color: "red", label: "Rejected" },
  Approved: { color: "green", label: "Approved" },
  Unlocked: { color: "blue", label: "Unlocked" },
  Archived: { color: "default", label: "Archived" },
};

const TYPE_LABELS = {
  scheme: "Scheme of Work",
  lesson_note: "Lesson Note",
  lesson_plan: "Lesson Plan",
  teaching_aid: "Teaching Aid",
  practical_guide: "Practical Guide",
};

const MaterialsTable = ({
  materials,
  loading,
  onView,
  onEdit,
  onDelete,
  onArchive,
  onSubmit,
}) => {
  const columns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      sorter: (a, b) => (a.title || "").localeCompare(b.title || ""),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (type) => <Tag color="purple">{TYPE_LABELS[type] || type}</Tag>,
    },
    {
      title: "Subject / Class",
      key: "subjectClass",
      render: (_, record) => {
        const subject = record.subjectName || "-";
        const cls = record.className || "";
        const stream = record.streamName ? ` (${record.streamName})` : "";
        return cls ? `${subject} – ${cls}${stream}` : subject;
      },
    },
    {
      title: "Academic Year",
      dataIndex: "academicYear",
      key: "academicYear",
      sorter: (a, b) =>
        (a.academicYear || "").localeCompare(b.academicYear || ""),
    },
    {
      title: "Term",
      dataIndex: "term",
      key: "term",
      render: (text) => text || "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      align: "center",
      filters: Object.keys(STATUS_TAGS).map((s) => ({
        text: STATUS_TAGS[s].label,
        value: s,
      })),
      onFilter: (value, record) => record.status === value,
      render: (status) => {
        const tag = STATUS_TAGS[status] || {
          color: "default",
          label: status || "Unknown",
        };
        return <Tag color={tag.color}>{tag.label}</Tag>;
      },
    },
    {
      title: "Submissions",
      dataIndex: "submissionCount",
      key: "submissionCount",
      align: "center",
      render: (v) => v ?? 0,
    },
    {
      title: "Delivered",
      dataIndex: "isDelivered",
      key: "isDelivered",
      align: "center",
      filters: [
        { text: "Delivered", value: true },
        { text: "Not delivered", value: false },
      ],
      onFilter: (value, record) => Boolean(record.isDelivered) === value,
      render: (_, record) => {
        if (record.isDelivered) {
          const tip = [
            record.deliveredAt
              ? dayjs(record.deliveredAt).format("DD MMM YYYY HH:mm")
              : null,
            record.deliveredByName || null,
          ]
            .filter(Boolean)
            .join(" · ");
          const badge = <Tag color="green">Delivered</Tag>;
          return tip ? <Tooltip title={tip}>{badge}</Tooltip> : badge;
        }
        return <Tag color="default">Not delivered</Tag>;
      },
    },
    {
      title: "Date",
      dataIndex: "created_at",
      key: "created_at",
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
      render: (date) => (date ? dayjs(date).format("DD MMM YYYY") : "-"),
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => onView(record)}
            />
          </Tooltip>
          {record.status === "Draft" && (
            <Tooltip title="Edit">
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => onEdit(record)}
              />
            </Tooltip>
          )}
          {record.status === "Draft" && (
            <Tooltip title="Submit for Approval">
              <Popconfirm
                title="Submit this material?"
                description="It will enter the approval workflow and become read-only."
                onConfirm={() => onSubmit(record)}
                okText="Submit"
                cancelText="Cancel"
              >
                <Button
                  type="text"
                  style={{ color: "#1677ff" }}
                  icon={<SendOutlined />}
                />
              </Popconfirm>
            </Tooltip>
          )}
          {record.status === "Unlocked" && !record.isDelivered && (
            <Tooltip title="Archive">
              <Popconfirm
                title="Archive this material?"
                description="This material will be archived."
                onConfirm={() => onArchive(record)}
                okText="Archive"
                cancelText="Cancel"
              >
                <Button
                  type="text"
                  icon={<InboxOutlined />}
                  style={{ color: "#8c8c8c" }}
                />
              </Popconfirm>
            </Tooltip>
          )}
          {record.status === "Draft" && (
            <Tooltip title="Delete">
              <Popconfirm
                title="Permanently delete this material?"
                description="This cannot be undone."
                onConfirm={() => onDelete(record)}
                okText="Delete"
                okButtonProps={{ danger: true }}
                cancelText="Cancel"
              >
                <Button type="text" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Table
      rowKey="id"
      columns={columns}
      dataSource={materials}
      loading={loading}
      pagination={{
        pageSize: 20,
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "50"],
      }}
      scroll={{ x: 1000 }}
      size="small"
    />
  );
};

export default MaterialsTable;
